import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from '@/hooks/useAuth';

interface Student {
  id: string;
  first_name?: string;
  last_name?: string;
  middle_name?: string;
  phone?: string;
  email?: string;
}

export const useStudentManagement = () => {
  const { user } = useAuth();
  const { toast } = useToast();

  const getGroupStudents = async (groupId: string): Promise<Student[]> => {
    try {
      // First get the student IDs from group_students
      const { data: groupStudentData, error: groupStudentError } = await supabase
        .from('group_students')
        .select('student_id')
        .eq('group_id', groupId);

      if (groupStudentError) throw groupStudentError;

      if (!groupStudentData || groupStudentData.length === 0) {
        return [];
      }

      // Get the student IDs
      const studentIds = groupStudentData.map(item => item.student_id);

      // Now get the profile data for these students including new fields
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, middle_name, phone, email')
        .in('id', studentIds);

      if (profileError) throw profileError;

      return profileData?.map(profile => ({
        id: profile.id,
        first_name: profile.first_name,
        last_name: profile.last_name,
        middle_name: profile.middle_name,
        phone: profile.phone,
        email: profile.email
      })) || [];
    } catch (error) {
      console.error('Error fetching group students:', error);
      return [];
    }
  };

  const addStudentToGroup = async (groupId: string, studentId: string) => {
    try {
      const { error } = await supabase
        .from('group_students')
        .insert({
          group_id: groupId,
          student_id: studentId
        });

      if (error) throw error;

      toast({
        title: "Հաջողություն",
        description: "Ուսանողը հաջողությամբ ավելացվեց խմբին"
      });
      
      return true;
    } catch (error) {
      console.error('Error adding student to group:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց ավելացնել ուսանողին խմբին",
        variant: "destructive"
      });
      return false;
    }
  };

  const removeStudentFromGroup = async (groupId: string, studentId: string) => {
    try {
      const { error } = await supabase
        .from('group_students')
        .delete()
        .eq('group_id', groupId)
        .eq('student_id', studentId);

      if (error) throw error;

      toast({
        title: "Հաջողություն",
        description: "Ուսանողը հաջողությամբ հեռացվեց խմբից"
      });
      
      return true;
    } catch (error) {
      console.error('Error removing student from group:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց հեռացնել ուսանողին խմբից",
        variant: "destructive"
      });
      return false;
    }
  };

  const createStudent = async (firstName: string, lastName: string, middleName: string, phone: string, email: string) => {
    if (!user) return { success: false, error: 'User not authenticated' };

    try {
      const response = await supabase.functions.invoke('create-student', {
        body: {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          middleName: middleName.trim(),
          phone: phone.trim(),
          email: email.trim()
        }
      });

      if (response.error) {
        throw new Error(response.error.message || 'Failed to create student');
      }

      toast({
        title: "Հաջողություն",
        description: "Ուսանողը հաջողությամբ ստեղծվեց։ Մուտքի տվյալները ուղարկվել են էլ. փոստով։"
      });

      return { success: true, data: response.data.student };
    } catch (error) {
      console.error('Error creating student:', error);
      toast({
        title: "Սխալ",
        description: error instanceof Error ? error.message : "Չհաջողվեց ստեղծել ուսանողին",
        variant: "destructive"
      });
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  };

  const deleteStudent = async (studentId: string) => {
    if (!user) return { success: false, error: 'User not authenticated' };

    try {
      const { data, error } = await supabase.rpc('delete_student_safely', {
        student_user_id: studentId
      });

      if (error) throw error;

      toast({
        title: "Հաջողություն",
        description: "Ուսանողը հաջողությամբ ջնջվեց"
      });

      return { success: true, data };
    } catch (error) {
      console.error('Error deleting student:', error);
      toast({
        title: "Սխալ",
        description: error instanceof Error ? error.message : "Չհաջողվեց ջնջել ուսանողին",
        variant: "destructive"
      });
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  };

  const updateStudent = async (studentId: string, studentData: {
    firstName: string;
    lastName: string;
    middleName: string;
    phone: string;
    email: string;
  }) => {
    if (!user) return { success: false, error: 'User not authenticated' };

    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          first_name: studentData.firstName.trim(),
          last_name: studentData.lastName.trim(),
          middle_name: studentData.middleName.trim(),
          phone: studentData.phone.trim(),
          email: studentData.email.trim()
        })
        .eq('id', studentId);

      if (error) throw error;

      toast({
        title: "Հաջողություն",
        description: "Ուսանողի տվյալները հաջողությամբ թարմացվեցին"
      });

      return { success: true };
    } catch (error) {
      console.error('Error updating student:', error);
      toast({
        title: "Սխալ",
        description: error instanceof Error ? error.message : "Չհաջողվեց թարմացնել ուսանողի տվյալները",
        variant: "destructive"
      });
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  };

  const getStudentInvitations = async () => {
    if (!user) return [];

    try {
      const { data, error } = await supabase
        .from('student_invitations')
        .select(`
          id,
          student_id,
          student_email,
          temporary_password,
          password_changed,
          created_at
        `)
        .eq('lecturer_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Get profile data separately for the invited students
      if (data && data.length > 0) {
        const studentIds = data.map(invitation => invitation.student_id);
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('id, first_name, last_name, middle_name, phone')
          .in('id', studentIds);

        if (profileError) {
          console.error('Error fetching profile data:', profileError);
          // Return invitations without profile data
          return data.map(invitation => ({
            id: invitation.id,
            student_id: invitation.student_id,
            email: invitation.student_email,
            first_name: '',
            last_name: '',
            middle_name: '',
            phone: '',
            temporary_password: invitation.temporary_password,
            password_changed: invitation.password_changed,
            created_at: invitation.created_at
          }));
        }

        // Merge invitation data with profile data
        return data.map(invitation => {
          const profile = profileData?.find(p => p.id === invitation.student_id);
          return {
            id: invitation.id,
            student_id: invitation.student_id,
            email: invitation.student_email,
            first_name: profile?.first_name || '',
            last_name: profile?.last_name || '',
            middle_name: profile?.middle_name || '',
            phone: profile?.phone || '',
            temporary_password: invitation.temporary_password,
            password_changed: invitation.password_changed,
            created_at: invitation.created_at
          };
        });
      }

      return [];
    } catch (error) {
      console.error('Error fetching student invitations:', error);
      return [];
    }
  };

  const findStudentByEmail = async (email: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, middle_name, phone, email')
        .ilike('email', email.trim())
        .limit(10);

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('Error finding student by email:', error);
      return [];
    }
  };

  const sendStudentCredentials = async (studentId: string, email: string) => {
    if (!user) return false;

    try {
      const response = await supabase.functions.invoke('send-student-credentials', {
        body: {
          studentId,
          email
        }
      });

      if (response.error) {
        throw new Error(response.error.message || 'Failed to send credentials');
      }

      return true;
    } catch (error) {
      console.error('Error sending student credentials:', error);
      throw error;
    }
  };

  return {
    getGroupStudents,
    addStudentToGroup,
    removeStudentFromGroup,
    createStudent,
    deleteStudent,
    updateStudent,
    getStudentInvitations,
    findStudentByEmail,
    sendStudentCredentials
  };
};
